import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Collapse,
  VStack,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiDollarSign, FiChevronDown, FiChevronRight, FiFileText, FiZap, FiLock, FiStar, FiPercent, FiCreditCard, FiUsers, FiCheckCircle, FiActivity, FiShield, FiBarChart, FiSettings, FiGift } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../constant";
import { useUserContext } from "../context/UserContext";
import { usePlanRestriction } from "../context/PlanRestrictionContext";
import { useGetPlanInfo } from "../services/planService";
import { useAccessControl } from "../hooks/useAccessControl";

const PayrollDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, canAccessPayroll } = useUserContext();
  const { showFeatureRestriction } = usePlanRestriction();
  const { planInfo } = useGetPlanInfo();
  const {
    isPayrollAdmin,
    isHRManager,
    isAccountant,
    isPayrollViewer,
    canAccessPayrollFeature,
    canCreateBatch,
    canApproveHR,
    canApproveAccountant,
    canManageFunding,
    canManageBankDetails,
    canViewPayrollSettings,
  } = useAccessControl();

  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  const activeBgColor = useColorModeValue("blue.50", "blue.900");
  const activeColor = useColorModeValue("blue.600", "blue.200");

  const isActive = pathname.startsWith('/payroll');

  // Auto-expand submenu when on any payroll page
  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    }
  }, [isActive]);

  const payrollItems = [
    {
      name: "Payroll Operations",
      path: "/payroll/enhanced",
      icon: FiActivity,
      description: "Comprehensive payroll management with multi-level approvals and international payments",
      canAccess: () => isPayrollAdmin() || isHRManager() || isAccountant() || isPayrollViewer()
    },
    {
      name: "Batch Management",
      path: ROUTES.batchManagement,
      icon: FiFileText,
      description: "Create and manage payroll batches",
      canAccess: () => canAccessPayrollFeature('batch_management')
    },
    {
      name: "HR Approvals",
      path: ROUTES.hrApprovals,
      icon: FiCheckCircle,
      description: "Review and approve payroll batches",
      canAccess: () => canAccessPayrollFeature('hr_approvals')
    },
    {
      name: "Accountant Approvals",
      path: ROUTES.accountantApprovals,
      icon: FiShield,
      description: "Financial review and approval",
      canAccess: () => canAccessPayrollFeature('accountant_approvals')
    },
    {
      name: "Wallet Management",
      path: ROUTES.walletManagement,
      icon: FiDollarSign,
      description: "Manage company wallet and funding",
      canAccess: () => canManageFunding()
    },
    {
      name: "Employee Bank Details",
      path: ROUTES.employeeBankDetails,
      icon: FiCreditCard,
      description: "Manage employee banking information",
      canAccess: () => canManageBankDetails()
    },
    {
      name: "Payroll Deductions",
      path: ROUTES.payrollDeductions,
      icon: FiPercent,
      description: "Manage and view payroll deductions",
      canAccess: () => canAccessPayrollFeature('payroll_deductions')
    },
    {
      name: "Payroll Allowances",
      path: ROUTES.payrollAllowances,
      icon: FiGift,
      description: "Manage BONUS and LEAVE_ALLOWANCE payments",
      canAccess: () => isPayrollAdmin() || isHRManager() || isAccountant() // Same as batch management
    },
    {
      name: "Settlement Management",
      path: ROUTES.settlementManagement,
      icon: FiShield,
      description: "Manage settlement accounts and payments",
      canAccess: () => isPayrollAdmin() || isHRManager() || isAccountant() // Same as batch management
    },
    {
      name: "Payroll Reports",
      path: ROUTES.payroll,
      icon: FiBarChart,
      description: "Traditional payroll reports and CSV export",
      canAccess: () => canAccessPayrollFeature('payroll_reports')
    },
    {
      name: "Payroll Settings",
      path: ROUTES.payrollSettings,
      icon: FiSettings,
      description: "Configure payroll period settings and dates",
      canAccess: () => canViewPayrollSettings() // Company Admin, PAYROLL_ADMIN, HR_MANAGER, ACCOUNTANT
    }
  ];

  // Filter out inaccessible payroll features
  const accessiblePayrollItems = payrollItems.filter(item => item.canAccess());

  const handleItemClick = (item) => {
    navigate(item.path);
    // Don't close the submenu - keep it open for easy navigation between items
  };

  // Don't render if user cannot access payroll (not Main Branch user)
  if (!canAccessPayroll()) {
    return null;
  }

  return (
    <Box>
      {/* Main Payroll Button */}
      <Flex
        align="center"
        justify="space-between"
        p={3}
        borderRadius="md"
        cursor="pointer"
        bg={isActive ? activeBgColor : "transparent"}
        color={isActive ? activeColor : "gray.700"}
        _hover={{ bg: hoverBgColor }}
        onClick={() => setIsOpen(!isOpen)}
        transition="all 0.2s"
      >
        <HStack spacing={3}>
          <Icon as={FiDollarSign} size={18} />
          <Text fontSize="sm" fontWeight="500">
            Payroll
          </Text>
        </HStack>
        <Icon
          as={isOpen ? FiChevronDown : FiChevronRight}
          size={16}
          transition="transform 0.2s"
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
        />
      </Flex>

      {/* Dropdown Items */}
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={1} align="stretch" mt={2} ml={6}>
          {accessiblePayrollItems.map((item) => {
            const isItemActive = pathname === item.path;
            return (
              <Flex
                key={item.path}
                align="center"
                p={2}
                borderRadius="md"
                cursor="pointer"
                bg={isItemActive ? activeBgColor : "transparent"}
                color={isItemActive ? activeColor : "gray.600"}
                _hover={{ bg: hoverBgColor }}
                onClick={() => handleItemClick(item)}
                transition="all 0.2s"
              >
                <HStack spacing={2} flex={1}>
                  <Icon as={item.icon} size={16} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" fontWeight="500">
                      {item.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {item.description}
                    </Text>
                  </VStack>
                </HStack>
              </Flex>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default PayrollDropdown;